/**
 * Anonymous funnel tracking (client side).
 *
 * Measures the visitor → signup → checkout journey with a random anonymous
 * visitor id stored in localStorage and a per-tab session id. No passwords,
 * payment details, email addresses or form values are ever sent.
 *
 * Only client-observable stages are sent from here (landing_view,
 * signup_view, signup_submitted). Server-confirmed stages (account_created,
 * checkout_started, checkout_completed, trial_activated, payment_succeeded)
 * are written by backend functions so real outcomes are distinguished from
 * button clicks.
 */

import { supabase } from "@/integrations/supabase/client";
import { getStoredUtm } from "@/lib/utmTracking";
import { BUILD_VERSION } from "@/main";

const VISITOR_KEY = "tm_funnel_visitor";
const SESSION_KEY = "tm_funnel_session";

export type ClientFunnelEvent = "landing_view" | "signup_view" | "signup_submitted";

function randomId(): string {
  try {
    return crypto.randomUUID();
  } catch {
    return `${Date.now()}-${Math.random().toString(36).slice(2)}`;
  }
}

/** Persistent anonymous visitor id (random, not derived from any personal data). */
export function getFunnelVisitorId(): string | null {
  if (typeof window === "undefined") return null;
  try {
    let id = localStorage.getItem(VISITOR_KEY);
    if (!id) {
      id = randomId();
      localStorage.setItem(VISITOR_KEY, id);
    }
    return id;
  } catch {
    return null;
  }
}

/** Per-tab session id (sessionStorage — a new tab is a new visit). */
export function getFunnelSessionId(): string | null {
  if (typeof window === "undefined") return null;
  try {
    let id = sessionStorage.getItem(SESSION_KEY);
    if (!id) {
      id = randomId();
      sessionStorage.setItem(SESSION_KEY, id);
    }
    return id;
  } catch {
    return null;
  }
}

/**
 * Sends a client funnel event. Fire-and-forget: failures are swallowed so
 * analytics never interfere with the user journey. Duplicates are collapsed
 * server-side by the (event, visitor, session) dedupe key.
 */
export function trackFunnelEvent(eventName: ClientFunnelEvent): void {
  const visitorId = getFunnelVisitorId();
  const sessionId = getFunnelSessionId();
  if (!visitorId || !sessionId) return;

  const utm = getStoredUtm();

  void supabase.functions
    .invoke("record-funnel-event", {
      body: {
        event_name: eventName,
        visitor_id: visitorId,
        session_id: sessionId,
        page_path: window.location.pathname.slice(0, 500),
        utm_source: utm?.utm_source ?? null,
        utm_medium: utm?.utm_medium ?? null,
        utm_campaign: utm?.utm_campaign ?? null,
        landing_page: utm?.landing_page ?? null,
        referrer: utm?.referrer ?? null,
        user_agent: navigator.userAgent.slice(0, 300),
        app_version: BUILD_VERSION,
      },
    })
    .catch(() => {
      /* analytics must never break the journey */
    });
}
